/**
 * The media devices available for use have changed.
 *
 * Information about the available media devices can be retrieved using the
 *    {@link media.getDevices} API.
 *
 * @public
 * @static
 * @memberof media
 * @event devices:change
 * @example
 * // Listen for changes to available media devices.
 * client.on('devices:change', function () {
 *    // Retrieve the latest media device lists.
 *    const devices = client.media.getDevices()
 * })
 */
export const DEVICES_CHANGED = 'devices:change'

/**
 * An error occurred while trying to access media devices.
 *
 * The most common causes of this error are when the browser does not have
 *    permission from the end-user to access the devices, or when the browser
 *    cannot find a media device that fulfills the
 *    {@link call.MediaConstraint MediaConstraint(s)} that was provided.
 *
 * @public
 * @static
 * @memberof media
 * @event devices:error
 * @param {Object} params
 * @param {Error} params.error The Basic error object.
 */
export const INITIALIZE_DEVICES_ERROR = 'devices:error'

/**
 * The specified Tracks have been muted.
 *
 * A Track can be muted using the {@link media.muteTracks} API.
 * @public
 * @memberof media
 * @event media:muted
 * @param {Object} params
 * @param {Array<string>} params.tracks The list of Tracks that were muted.
 */
export const TRACKS_MUTED = 'media:muted'

/**
 * The specified Tracks have been unmuted.
 *
 * A Track can be unmuted using the {@link media.unmuteTracks}
 *    API.
 * @public
 * @memberof media
 * @event media:unmuted
 * @param {Object} params
 * @param {Array<string>} params.tracks The list of Tracks that were unmuted.
 */
export const TRACKS_UNMUTED = 'media:unmuted'

/**
 * The specified Track has had its media source muted.
 *
 * The Track is still active, but is not receiving media any longer. An audio
 *    track will be silent and a video track will be a black frame. It is
 *    possible for the track to start receiving media again (see the
 *    {@link media.event:media:sourceUnmuted media:sourceUnmuted} event).
 *
 * This event is generated outside the control of the SDK. This will predominantely
 *    happen for a remote track during network issues, where media will lose frames
 *    and be "choppy". This may also happen for a local track if the browser or
 *    end-user stops allowing the SDK to access the media device, for example.
 * @public
 * @static
 * @memberof media
 * @event media:sourceMuted
 * @param {Object} params
 * @param {string} params.trackId The track that is affected as a result of media source being muted.
 */
export const TRACK_SOURCE_MUTED = 'media:sourceMuted'

/**
 * The specified Track has started receiving media from its source once again.
 *
 * The Track returns to the state before it was muted (see the
 *    {@link media.event:media:sourceMuted media:sourceMuted} event), and will
 *    be able to display video or play audio once again.
 *
 * This event is generated outside the control of the SDK, when the cause of the
 *    media source being muted had been undone.
 * @public
 * @static
 * @memberof media
 * @event media:sourceUnmuted
 * @param {Object} params
 * @param {string} params.trackId The track that is affected as a result of media source being unmuted.
 */
export const TRACK_SOURCE_UNMUTED = 'media:sourceUnmuted'

/**
 * The specified Track has been rendered into an element.
 *
 * @public
 * @static
 * @memberof media
 * @event media:trackRendered
 * @param {Object} params
 * @param {Array<string>} params.trackIds The list of track id's that were rendered.
 * @param {string} params.selector The css selector used to identify the element the track is rendered into.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 */
export const TRACK_RENDERED = 'media:trackRendered'

/**
 * A local Track has ended unexpectedly. The Track may still be part of a Call but
 *    has become disconnected from its media source and is not recoverable.
 *
 * This event is emitted when an action other than an SDK operation stops the
 *    track. The most comon scenarios are when a device being used for a Call
 *    disconnects, any local tracks (such as audio from a bluetooth headset's
 *    microphone or video from a USB camera) from that device will be ended.
 *    Another scenario is for screensharing, where some browsers provide the
 *    ability to stop screensharing directly rather than through an SDK operation.
 *
 * When a local track ends this way, it will still be part of the Call but will
 *    not have any media. The track can be removed from the call with the
 *    {@link call.removeMedia} API so the remote side of the Call knows the track
 *    has stopped, or the track can be replaced with a new track using the
 *    {@link call.replaceTrack} API to prevent any interruption.
 * @public
 * @static
 * @memberof media
 * @event media:trackEnded
 * @param {Object} params
 * @param {Object} params.trackId The Track that has ended.
 * @param {Object} params.callId The ID of the Call the Track is used in.
 */
export const TRACK_ENDED = 'media:trackEnded'
